/**
 * Conteúdo fictício por tela da demonstração (navegação /demo/...).
 */

import { localizeDemoRich } from './localizeRichContent'

const SYNC = 'Última sincronização simulada: hoje às 09:18 · America/São_Paulo'

/**
 * @param {string} title
 * @param {Array} rows — linhas da tabela + lista de contatos
 * @param {Array<Array<{from: 'them'|'me'|'system', text: string, time: string}>>} [messageSets] — uma lista de mensagens por contato (mesma ordem que rows)
 * @param {{
 *   kpis?: Array<{label: string, value: string, hint?: string}>,
 *   insights?: Array<{title: string, text: string}>,
 *   timeline?: Array<{at: string, text: string}>,
 *   socialProofStrip?: Array<{stat: string, label: string}>
 * }} [extra]
 */
function chatScreen(title, rows, messageSets, extra = {}) {
  const medias = rows.filter((r) => r.tipo !== 'texto').length
  const first = rows[0] || {}
  const chatContacts = rows.map((r, i) => ({
    id: i + 1,
    name: r.contato,
    preview: r.preview,
    time: r.quando,
    unseen: r.status === 'novo' ? 2 : 0
  }))
  const fallbackMsgs = (row) => [
    { from: 'them', text: row.preview || 'Mensagem de exemplo (demonstração).', time: row.quando || '09:10' },
    { from: 'me', text: 'Ok, anotado aqui.', time: '09:11' },
    { from: 'them', text: 'Qualquer dado aqui é fictício.', time: '09:12' },
    { from: 'system', text: 'Demonstração — sem dados reais.', time: '09:13' }
  ]
  const chatThreads = {}
  rows.forEach((r, i) => {
    const id = i + 1
    chatThreads[id] =
      messageSets && messageSets[i] && messageSets[i].length ? messageSets[i] : fallbackMsgs(r)
  })
  const chatMessages = chatThreads[1] || fallbackMsgs(first)
  const defaultKpis = [
    { label: 'Threads (24h)', value: String(rows.length), hint: 'conversas fictícias' },
    { label: 'Mídias', value: String(medias), hint: 'foto / vídeo / áudio' },
    { label: 'Grupos monitorados', value: '6', hint: 'nomes de exemplo' }
  ]
  const defaultInsights = [
    { title: 'Resumo automático (demo)', text: 'Detectamos picos de mensagens entre 19h e 22h, em linha com o padrão típico de uso noturno (dados simulados).' },
    { title: 'Palavras-chave (demo)', text: 'reunião, entrega, chave PIX, código — apenas ilustração de alertas que o painel real pode destacar.' }
  ]
  const defaultTimeline = [
    { at: '09:05', text: 'Sincronização incremental simulada (somente demonstração).' },
    { at: '08:40', text: 'Novo grupo fictício indexado: "Projeto Atlas — interno".' }
  ]
  return {
    demoLayout: 'chat',
    chatHeadline: title.replace(/\s*\(demonstração\)\s*$/, '').replace(/\s*\(demo\)\s*$/, ''),
    chatContacts,
    chatThreads,
    chatMessages,
    title,
    subtitle: SYNC,
    kpis: extra.kpis || defaultKpis,
    insights: extra.insights || defaultInsights,
    ...(extra.socialProofStrip ? { socialProofStrip: extra.socialProofStrip } : {}),
    mainTable: {
      columns: [
        { field: 'quando', title: 'Quando' },
        { field: 'contato', title: 'Contato / grupo' },
        { field: 'preview', title: 'Prévia' },
        { field: 'tipo', title: 'Tipo' },
        { field: 'status', title: 'Status' }
      ],
      rows
    },
    timeline: extra.timeline !== undefined ? extra.timeline : defaultTimeline,
    footnote: 'Nomes, horários e textos são fictícios. Nenhum dado real de terceiros é exibido ou coletado nesta rota.'
  }
}

const RICH = {
  whatsappChat: chatScreen(
    'WhatsApp — conversas (demonstração)',
    [
      { quando: '09:14', contato: 'Grupo · Família Silva', preview: 'Mãe: almoço domingo 13h confirmado 🍖', tipo: 'texto', status: 'lido' },
      { quando: '09:08', contato: 'Carlos Mendes · Comercial', preview: '🎤 Áudio 0:42 — revisão do contrato', tipo: 'áudio', status: 'entregue' },
      { quando: '08:59', contato: 'Grupo · Pelada quinta', preview: 'Rafa: falta 1 goleiro, quem vem?', tipo: 'texto', status: 'novo' },
      { quando: '08:52', contato: 'Dra. Helena · Clínica', preview: 'Lembrete: consulta amanhã 14:20', tipo: 'texto', status: 'lido' },
      { quando: '08:44', contato: '+55 21 9****-**03', preview: 'Seu código Uber: 7731', tipo: 'texto', status: 'novo' },
      { quando: '08:31', contato: 'Grupo · Projeto Atlas', preview: 'Marina enviou Relatorio_Q1.pdf', tipo: 'documento', status: 'lido' },
      { quando: '08:18', contato: 'Pizza Sul delivery', preview: 'Pedido #8842 saiu para entrega — 25–35 min', tipo: 'texto', status: 'lido' },
      { quando: '08:05', contato: 'Escola Aurora (secretaria)', preview: 'Reunião de pais adiada para 12/02', tipo: 'texto', status: 'lido' },
      { quando: 'Ontem 22:10', contato: 'Spam · Prêmio falso', preview: 'Parabéns! Você ganhou um iPhone…', tipo: 'texto', status: 'lido' },
      { quando: 'Ontem 19:02', contato: 'Banco Demo SAC', preview: 'Fatura fechada R$ 1.284,90 — venc. 10/02', tipo: 'texto', status: 'lido' }
    ],
    [
      [
        { from: 'them', text: 'Mãe: pessoal, almoço de domingo na casa da vó, cheguem até 12:30.', time: '09:02' },
        { from: 'me', text: 'Eu levo sobremesa, pode ser bolo de chocolate?', time: '09:03' },
        { from: 'them', text: 'Tia Carla: eu levo suco e salada de fruta.', time: '09:05' },
        { from: 'them', text: 'Mãe: almoço domingo 13h confirmado 🍖 — conto com todo mundo.', time: '09:14' },
        { from: 'me', text: 'Combinado! 🙌', time: '09:15' }
      ],
      [
        { from: 'them', text: 'Carlos: bom dia! Conseguiu ver a planilha de volumes?', time: '08:50' },
        { from: 'me', text: 'Bom dia Carlos, sim — os números batem com a proposta v3.', time: '08:52' },
        { from: 'them', text: '🎤 Áudio 0:42 — revisão do contrato (só demo, sem arquivo real).', time: '09:08' },
        { from: 'me', text: 'Perfeito, repasso pro jurídido hoje à tarde.', time: '09:09' },
        { from: 'them', text: 'Show. Qualquer coisa me chama no ramal 402.', time: '09:10' }
      ],
      [
        { from: 'them', text: 'Rafa: galera, quinta 20h no society municipal.', time: 'Ontem 20:15' },
        { from: 'me', text: 'Eu vou de linha, posso levar bola?', time: 'Ontem 20:18' },
        { from: 'them', text: 'Rafa: falta 1 goleiro, quem vem?', time: '08:59' },
        { from: 'them', text: 'Lucas: eu fecho o gol, mas chego 20:15.', time: '09:00' },
        { from: 'me', text: 'Contem comigo de campo.', time: '09:01' }
      ],
      [
        { from: 'them', text: 'Clínica: Olá, sua consulta está agendada.', time: '08:30' },
        { from: 'me', text: 'Posso remarcar para a mesma hora na próxima semana?', time: '08:40' },
        { from: 'them', text: 'Lembrete: consulta amanhã 14:20 — confirme presença.', time: '08:52' },
        { from: 'me', text: 'Confirmo presença. Obrigado!', time: '08:53' }
      ],
      [
        { from: 'them', text: 'Uber: seu motorista está a caminho.', time: '08:40' },
        { from: 'system', text: 'Seu código Uber: 7731 (demonstração).', time: '08:44' },
        { from: 'me', text: 'Valeu, já estou na calçada.', time: '08:45' }
      ],
      [
        { from: 'them', text: 'Marina: subi o PDF no link interno (simulado).', time: '08:20' },
        { from: 'me', text: 'Baixei aqui, vou comentar nas células D12–D18.', time: '08:25' },
        { from: 'them', text: 'Marina enviou Relatorio_Q1.pdf', time: '08:31' },
        { from: 'them', text: 'João: time de dados validou os gráficos.', time: '08:33' },
        { from: 'me', text: 'Ótimo. Fecho a versão até 17h.', time: '08:35' }
      ],
      [
        { from: 'them', text: 'Pizza Sul: recebemos seu pedido #8842.', time: '08:10' },
        { from: 'them', text: 'Pedido #8842 saiu para entrega — 25–35 min', time: '08:18' },
        { from: 'me', text: 'Pode deixar na portaria, obrigado!', time: '08:19' },
        { from: 'them', text: 'Entregador chegou — confirme no app.', time: '08:28' }
      ],
      [
        { from: 'them', text: 'Secretaria: boletim do 3º bimestre disponível no portal.', time: 'Seg' },
        { from: 'me', text: 'Obrigado, vou conferir hoje à noite.', time: 'Seg' },
        { from: 'them', text: 'Reunião de pais adiada para 12/02', time: '08:05' }
      ],
      [
        { from: 'them', text: 'Parabéns! Você ganhou um iPhone 15… clique aqui 👇', time: '22:08' },
        { from: 'system', text: 'Mensagem suspeita bloqueada na demo — não clique em links desconhecidos.', time: '22:09' }
      ],
      [
        { from: 'them', text: 'Banco Demo: sua fatura está disponível no app.', time: 'Ontem 10:00' },
        { from: 'them', text: 'Fatura fechada R$ 1.284,90 — venc. 10/02', time: 'Ontem 19:02' },
        { from: 'me', text: 'Já agendei o pagamento via PIX.', time: 'Ontem 19:10' }
      ]
    ],
    {
      kpis: [
        { label: 'Mensagens indexadas (7d)', value: '2.891', hint: 'texto · sticker · documento' },
        { label: 'Contatos únicos', value: '71', hint: 'chats diretos fictícios' },
        { label: 'Grupos ativos no demo', value: '14', hint: 'família · trabalho · comunidade' },
        { label: 'Notificações fortes (18h–23h)', value: '76%', hint: 'padrão típico · ilustrativo' }
      ],
      insights: [
        {
          title: 'Uso no Brasil (simulado)',
          text: 'Neste cenário fictício, grupos de escola e parentais respondem por boa parte das conversas em horário de pátio e noite — bom para testar alertas de palavra-chave.'
        },
        {
          title: 'PIX e códigos',
          text: 'O painel pode destacar mensagens com “Pix”, “transferência” ou códigos numéricos de banco; aqui é só demonstração de como os cartões de insight aparecem.'
        }
      ],
      socialProofStrip: [
        { stat: '4,9★', label: 'Nota ilustrativa entre quem ativou o modo família (valor fictício)' },
        { stat: '+210k', label: 'Menções agregadas em comunidades e reviews (exemplo de escala)' },
        { stat: '#1', label: 'WhatsApp lidera mensagens instantâneas na amostra demo (Brasil)' }
      ],
      timeline: [
        { at: '09:12', text: '186 registros fictícios espelhados no contador de WhatsApp do menu (demo).' },
        { at: '08:55', text: 'Sincronização incremental simulada — áudios e chamadas em abas separadas.' }
      ]
    }
  ),

  whatsappAudio: {
    title: 'WhatsApp — áudios (demonstração)',
    subtitle: SYNC,
    kpis: [
      { label: 'Áudios (7 dias)', value: '34', hint: 'duração total ~ 42 min' },
      { label: 'Média', value: '74s', hint: 'por mensagem' },
      { label: 'Maior arquivo', value: '3m 10s', hint: 'grupo trabalho' }
    ],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'de', title: 'De' },
        { field: 'dur', title: 'Duração' },
        { field: 'onde', title: 'Contexto' }
      ],
      rows: [
        { hora: '09:04', de: 'Carlos', dur: '0:34', onde: 'Chat direto' },
        { hora: '08:22', de: 'Grupo Atlas', dur: '1:12', onde: 'Grupo (12 ouvintes)' },
        { hora: 'Ontem 21:40', de: 'Marina', dur: '0:18', onde: 'Chat direto' },
        { hora: 'Ontem 19:05', de: 'Família Silva', dur: '2:58', onde: 'Grupo' }
      ]
    },
    footnote: 'Não há reprodução de áudio real — apenas metadados fictícios.'
  },

  whatsappCall: {
    title: 'WhatsApp — chamadas VoIP (demonstração)',
    subtitle: SYNC,
    kpis: [
      { label: 'Chamadas (7d)', value: '12', hint: 'voz / vídeo' },
      { label: 'Perdidas', value: '2', hint: 'fora de horário' },
      { label: 'Maior sessão', value: '18m', hint: 'vídeo · grupo' }
    ],
    mainTable: {
      columns: [
        { field: 'inicio', title: 'Início' },
        { field: 'tipo', title: 'Tipo' },
        { field: 'contato', title: 'Contato' },
        { field: 'duracao', title: 'Duração' }
      ],
      rows: [
        { inicio: '08:57', tipo: 'Voz', contato: '+55 11 9****-**44', duracao: '4m 12s' },
        { inicio: 'Ontem 20:12', tipo: 'Vídeo', contato: 'Marina', duracao: '18m 01s' },
        { inicio: 'Ontem 18:02', tipo: 'Voz', contato: 'Grupo Atlas', duracao: '6m 33s' }
      ]
    },
    footnote: 'Registros simulados de chamadas pelo app WhatsApp.'
  },

  instagramChat: chatScreen(
    'Instagram — DMs (demonstração)',
    [
      { quando: '09:17', contato: '@studio.aurora', preview: 'Te marquei no post do backstage 📸', tipo: 'texto', status: 'novo' },
      { quando: '09:03', contato: '@cafe.district', preview: 'Cupom AMIGO15 válido até sexta', tipo: 'texto', status: 'lido' },
      { quando: '08:48', contato: 'marina.fotos', preview: 'Reels: making of do casamento', tipo: 'link', status: 'lido' },
      { quando: '08:30', contato: '@bike.urban', preview: 'Story: pedal de domingo saiu do ar?', tipo: 'story', status: 'novo' },
      { quando: '08:12', contato: 'Collab · BeautyLab', preview: 'Briefing da campanha #skincare no Drive', tipo: 'documento', status: 'lido' },
      { quando: 'Ontem 23:40', contato: 'Close Friends · Lu', preview: 'Foto: “só pra CF” — jantar surpresa', tipo: 'imagem', status: 'lido' },
      { quando: 'Ontem 18:05', contato: '@loja.vintage.sp', preview: 'Peça reservada: jaqueta jeans 42', tipo: 'texto', status: 'lido' },
      { quando: 'Ontem 11:20', contato: '@memesdemae', preview: 'Repost: quando o filho pede “só mais 5 min”', tipo: 'texto', status: 'lido' }
    ],
    [
      [
        { from: 'them', text: 'Oi! Conseguimos o horário do estúdio das 15h às 18h.', time: 'Ontem 16:22' },
        { from: 'me', text: 'Maravilha, levo os adereços que combinamos.', time: 'Ontem 16:30' },
        { from: 'them', text: 'Perfeito — te marquei no post do backstage 📸', time: '09:17' },
        { from: 'me', text: 'Já vi, repostei nos stories (demo).', time: '09:18' }
      ],
      [
        { from: 'them', text: 'Oi! Vi que você curtiu nosso último Reels ☕', time: '08:55' },
        { from: 'me', text: 'Achei incrível a degustação de grãos.', time: '08:58' },
        { from: 'them', text: 'Cupom AMIGO15 válido até sexta — só no balcão.', time: '09:03' },
        { from: 'me', text: 'Vou passar sábado de manhã!', time: '09:04' }
      ],
      [
        { from: 'them', text: 'Marina: subi o making of em 4K no Drive (link fictício).', time: '08:20' },
        { from: 'me', text: 'A edição de cor ficou impecável.', time: '08:25' },
        { from: 'them', text: 'Reels: making of do casamento — confere o áudio novo.', time: '08:48' },
        { from: 'them', text: 'Cliente aprovou a capa do álbum 🙌', time: '08:50' }
      ],
      [
        { from: 'them', text: 'Pedal domingo: saída 7h do parque Ibirapuera.', time: 'Dom 19:00' },
        { from: 'me', text: 'Conta comigo, levo kit de reparo.', time: 'Dom 19:05' },
        { from: 'them', text: 'Story: pedal de domingo saiu do ar? Reenviei o roteiro.', time: '08:30' },
        { from: 'me', text: 'Recebi, salvei no Maps offline.', time: '08:32' }
      ],
      [
        { from: 'them', text: 'Segue o briefing + moodboard (simulado).', time: 'Ontem 15:00' },
        { from: 'me', text: 'Incluí notas de tom de voz no doc.', time: 'Ontem 15:40' },
        { from: 'them', text: 'Briefing da campanha #skincare no Drive — versão 2.', time: '08:12' },
        { from: 'me', text: 'Aprovado do meu lado. 🚀', time: '08:14' }
      ],
      [
        { from: 'them', text: 'Lu: não reposta, tá? É surpresa pro aniversário dele.', time: '23:35' },
        { from: 'me', text: 'Fechado, segredo absoluto 🤫', time: '23:36' },
        { from: 'them', text: 'Foto: “só pra CF” — jantar surpresa', time: '23:40' },
        { from: 'me', text: 'Amei o lugar, decoração 10/10.', time: '23:41' }
      ],
      [
        { from: 'them', text: 'Jaqueta jeans tamanho 42 reservada por 24h.', time: 'Ontem 17:50' },
        { from: 'me', text: 'Confirmo a reserva, pago via PIX.', time: 'Ontem 18:00' },
        { from: 'them', text: 'Peça reservada: jaqueta jeans 42 — retirada na Lapa.', time: 'Ontem 18:05' }
      ],
      [
        { from: 'them', text: 'Enquete: pior desculpa de criança pra não dormir?', time: 'Ontem 10:00' },
        { from: 'me', text: '“Só mais 5 minutos” em loop infinito 😂', time: 'Ontem 10:05' },
        { from: 'them', text: 'Repost: quando o filho pede “só mais 5 min” — marcou?', time: 'Ontem 11:20' }
      ]
    ],
    {
      kpis: [
        { label: 'DMs ativas (7d)', value: '184', hint: 'Inbox + requests fictícios' },
        { label: 'Campanhas / collabs', value: '12', hint: 'marcas e criadores (demo)' },
        { label: 'Stories & Reels citados', value: '47', hint: 'links e reações simuladas' },
        { label: 'Engajamento noturno', value: '+22%', hint: 'vs. tarde — amostra ilustrativa' }
      ],
      insights: [
        {
          title: 'Criadores e marcas',
          text: 'DMs costumam concentrar fechamento de parceria e envio de briefings; o demo mostra como separar thread comercial de conversa pessoal.'
        },
        {
          title: 'Close Friends',
          text: 'Conteúdo restrito (CF) aparece com rótulo específico — útil para ensinar o que o painel real pode isolar em relatórios.'
        }
      ],
      socialProofStrip: [
        { stat: '68%', label: 'Dos “alertas” fictícios vieram de DM de marca ou collab (demo)' },
        { stat: '3,2x', label: 'Mais respostas após story com enquete — benchmark ilustrativo' },
        { stat: '72', label: 'Registros de Instagram no contador demo do menu lateral' }
      ],
      timeline: [
        { at: '09:15', text: 'Novo request de DM de @studio.aurora simulado na fila de sincronização.' },
        { at: '08:50', text: 'Reels com link externo marcado como mídia rica (demonstração).' }
      ]
    }
  ),

  telegramChat: chatScreen(
    'Telegram — grupos e canais (demonstração)',
    [
      { quando: '09:19', contato: 'Canal · TechBR', preview: 'Breaking: patch de segurança Android', tipo: 'post', status: 'lido' },
      { quando: '09:01', contato: 'Grupo · Infra interna', preview: 'Canary deploy OK — métricas verdes', tipo: 'texto', status: 'novo' },
      { quando: '08:40', contato: 'Bot · PagamentosDemo', preview: 'PIX recebido R$ 189,90 — ref. 8821', tipo: 'bot', status: 'lido' },
      { quando: '08:22', contato: 'Grupo · Pais & Escola', preview: 'Prof: prova de matemática adiada', tipo: 'texto', status: 'lido' },
      { quando: '08:05', contato: 'Canal · Mercado Hoje', preview: 'Ibovespa +0,8% no pré-abertura', tipo: 'post', status: 'lido' },
      { quando: 'Ontem 21:50', contato: 'Grupo · Corrida 10k', preview: 'Link do chip de peito no grupo fixado', tipo: 'texto', status: 'lido' },
      { quando: 'Ontem 19:30', contato: 'Chat secreto · Ana', preview: 'Mensagem com timer 1 min (simulado)', tipo: 'texto', status: 'lido' },
      { quando: 'Ontem 14:12', contato: 'Canal · Receitas rápidas', preview: 'Vídeo: macarrão de uma panela', tipo: 'post', status: 'lido' }
    ],
    [
      [
        { from: 'them', text: 'TechBR: artigo “Kotlin 2.0 chegou” — leitura 8 min.', time: '08:50' },
        { from: 'them', text: 'Breaking: patch de segurança Android — reinicie quando puder.', time: '09:19' },
        { from: 'me', text: 'Salvei o link no Pocket (demo).', time: '09:20' }
      ],
      [
        { from: 'them', text: 'SRE: janela de deploy 21h–23h, avisem squads dependentes.', time: 'Ontem 18:00' },
        { from: 'me', text: 'Time checkout deu OK no smoke test.', time: '20:30' },
        { from: 'them', text: 'Canary deploy OK — métricas verdes', time: '09:01' },
        { from: 'them', text: 'Promovendo pra produção às 22:10.', time: '09:02' },
        { from: 'me', text: 'De olho no painel APM aqui.', time: '09:03' }
      ],
      [
        { from: 'them', text: 'PagamentosDemo: gerando comprovante…', time: '08:35' },
        { from: 'system', text: 'PIX recebido R$ 189,90 — ref. 8821 (demonstração).', time: '08:40' },
        { from: 'them', text: 'Recibo enviado por e-mail simulado.', time: '08:41' }
      ],
      [
        { from: 'them', text: 'Coordenação: lembrando reunião pedagógica quinta.', time: 'Seg 08:00' },
        { from: 'me', text: 'Confirmo presença online.', time: 'Seg 08:05' },
        { from: 'them', text: 'Prof: prova de matemática adiada para terça que vem.', time: '08:22' },
        { from: 'them', text: 'Pais: obrigada pelo aviso!', time: '08:25' }
      ],
      [
        { from: 'them', text: 'Pré-abertura: fluxo estrangeiro comprador.', time: '08:00' },
        { from: 'them', text: 'Ibovespa +0,8% no pré-abertura', time: '08:05' },
        { from: 'me', text: 'Vou acompanhar o after-market.', time: '08:06' }
      ],
      [
        { from: 'them', text: 'Organização: retirada de kits sábado 8h–12h.', time: 'Qui 20:00' },
        { from: 'me', text: 'Consigo buscar meu kit às 9h.', time: 'Sex 07:30' },
        { from: 'them', text: 'Link do chip de peito no grupo fixado — confiram número.', time: 'Ontem 21:50' }
      ],
      [
        { from: 'them', text: 'Ana: posso falar contigo um minuto?', time: 'Ontem 19:10' },
        { from: 'me', text: 'Claro, manda.', time: 'Ontem 19:12' },
        { from: 'them', text: 'Mensagem com timer 1 min (simulado) — conteúdo apagado na vida real.', time: 'Ontem 19:30' },
        { from: 'system', text: 'Chat secreto: apenas ilustração de UI.', time: 'Ontem 19:31' }
      ],
      [
        { from: 'them', text: 'Novo vídeo: macarrão de uma panela em 12 min.', time: 'Ontem 14:00' },
        { from: 'them', text: 'Ingredientes na descrição do canal.', time: 'Ontem 14:10' },
        { from: 'me', text: 'Salvei pra fazer no fim de semana.', time: 'Ontem 14:12' }
      ]
    ],
    {
      kpis: [
        { label: 'Mensagens em grupos (7d)', value: '1.024', hint: 'texto · mídia · fixados' },
        { label: 'Canais acompanhados', value: '11', hint: 'Tech · mercado · lifestyle' },
        { label: 'Bots acionados', value: '418', hint: 'pagamentos · moderação (fictício)' },
        { label: 'Membros combinados', value: '3,2k', hint: 'soma ilustrativa de audiência' }
      ],
      insights: [
        {
          title: 'Canais vs. grupos',
          text: 'No demo, canais trazem broadcast e grupos trazem discussão em tempo real — o painel real pode filtrar por tipo de chat.'
        },
        {
          title: 'Chat secreto e bots',
          text: 'Timers e comprovantes via bot são exemplos de conteúdo sensível; use a demo para explicar políticas de privacidade ao cliente final.'
        }
      ],
      socialProofStrip: [
        { stat: '41', label: 'Threads Telegram no contador demo do menu — volume visível' },
        { stat: '92%', label: 'Dos usuários demo “viram” primeiro o canal antes do grupo (ilustrativo)' },
        { stat: '24/7', label: 'Fluxo típico de grupos de trabalho e escola em fusos BR (fictício)' }
      ],
      timeline: [
        { at: '09:18', text: 'Post fixado no canal TechBR atualizado (simulação de push).' },
        { at: '08:42', text: 'Bot PagamentosDemo confirmou transação fictícia — ref. 8821.' }
      ]
    }
  ),

  tinderChat: chatScreen('Tinder — conversas (demonstração)', [
    { quando: '08:48', contato: 'Match · “Lu”', preview: 'Oi! Vi que curte trilha também 🙂', tipo: 'texto', status: 'novo' },
    { quando: 'Ontem', contato: 'Match · “Pedro”', preview: 'Te mandei um áudio contando do show', tipo: 'áudio', status: 'lido' }
  ]),

  kwaiChat: chatScreen('Kwai — mensagens (demonstração)', [
    { quando: '08:30', contato: 'Creator Hub', preview: 'Sua meta semanal foi batida 🎉', tipo: 'texto', status: 'lido' },
    { quando: '08:05', contato: 'Fan · @ana.m', preview: 'Adorei o vídeo de receita!', tipo: 'texto', status: 'lido' }
  ]),

  badooChat: chatScreen('Badoo — chat (demonstração)', [
    { quando: '07:55', contato: 'Match · “Rafa”', preview: 'Bora tomar um café sábado?', tipo: 'texto', status: 'novo' }
  ]),

  snapchatChat: chatScreen('Snapchat — conversas (demonstração)', [
    { quando: '08:12', contato: 'Mel 🌸', preview: 'Snap de viagem — aeroporto', tipo: 'snap', status: 'aberto' },
    { quando: 'Ontem', contato: 'Streak grupo', preview: 'Streak 120 dias mantido', tipo: 'sistema', status: 'lido' }
  ]),

  mobileApps: {
    title: 'Aplicativos instalados (demonstração)',
    subtitle: 'Lista parcial simulada com versões e tempo de uso diário.',
    kpis: [
      { label: 'Apps totais', value: '118', hint: 'usuário + sistema' },
      { label: 'Com notificação', value: '54', hint: 'últimas 24h' },
      { label: 'Bloqueados (demo)', value: '3', hint: 'política parental simulada' }
    ],
    mainTable: {
      columns: [
        { field: 'app', title: 'Aplicativo' },
        { field: 'pkg', title: 'Pacote' },
        { field: 'ver', title: 'Versão' },
        { field: 'uso', title: 'Uso hoje' },
        { field: 'cat', title: 'Categoria' }
      ],
      rows: [
        { app: 'WhatsApp', pkg: 'com.whatsapp', ver: '2.24.8.81', uso: '2h 14m', cat: 'Comunicação' },
        { app: 'Instagram', pkg: 'com.instagram.android', ver: '312.0.0.0', uso: '1h 02m', cat: 'Social' },
        { app: 'Chrome', pkg: 'com.android.chrome', ver: '124.0.6367', uso: '48m', cat: 'Internet' },
        { app: 'Maps', pkg: 'com.google.android.apps.maps', ver: '11.120.0', uso: '22m', cat: 'Mapas' },
        { app: 'Banco Demo', pkg: 'br.com.bancodemo', ver: '8.4.1', uso: '6m', cat: 'Finanças' }
      ]
    },
    footnote: 'Pacotes e versões são exemplos genéricos.'
  },

  mobileCalls: {
    title: 'Chamadas telefônicas (demonstração)',
    subtitle: 'Registro SIM + identificação simulada.',
    kpis: [
      { label: 'Realizadas', value: '14', hint: '7 dias' },
      { label: 'Recebidas', value: '22', hint: '7 dias' },
      { label: 'Perdidas', value: '5', hint: 'spam provável' }
    ],
    mainTable: {
      columns: [
        { field: 'quando', title: 'Quando' },
        { field: 'numero', title: 'Número / contato' },
        { field: 'tipo', title: 'Tipo' },
        { field: 'dur', title: 'Duração' }
      ],
      rows: [
        { quando: '08:58', numero: '+55 11 4002-8922', tipo: 'Saída', dur: '4m 12s' },
        { quando: '08:12', numero: 'Banco Demo SAC', tipo: 'Entrada', dur: '2m 01s' },
        { quando: 'Ontem 19:44', numero: '+55 21 9****-**11', tipo: 'Entrada', dur: '0s (perdida)' }
      ]
    },
    footnote: 'Números mascarados ou genéricos.'
  },

  keylogger: {
    title: 'Teclas digitadas (demonstração)',
    subtitle: 'Buffer fictício de teclado com app de origem.',
    kpis: [
      { label: 'Teclas (24h)', value: '1.204', hint: 'inclui correções' },
      { label: 'Apps', value: '9', hint: 'com foco de texto' },
      { label: 'Campos sensíveis', value: '2', hint: 'mascarados na UI real' }
    ],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'app', title: 'App' },
        { field: 'campo', title: 'Campo' },
        { field: 'texto', title: 'Trecho (demo)' }
      ],
      rows: [
        { hora: '09:18', app: 'Chrome', campo: 'Busca', texto: 'como ativar rastreamento parental' },
        { hora: '09:05', app: 'Notas', campo: 'Título', texto: 'Lista mercado — feira sábado' },
        { hora: '08:40', app: 'Banco Demo', campo: 'Senha 6 dígitos', texto: '••••••' }
      ]
    },
    footnote: 'Na versão real, campos sensíveis podem ser mascarados conforme política.'
  },

  contacts: {
    title: 'Contatos da agenda (demonstração)',
    subtitle: 'Agenda fictícia mesclada com WhatsApp.',
    kpis: [
      { label: 'Contatos', value: '312', hint: 'agenda + SIM' },
      { label: 'Duplicados', value: '14', hint: 'sugestão de fusão' },
      { label: 'Sem foto', value: '201', hint: 'ilustração' }
    ],
    mainTable: {
      columns: [
        { field: 'nome', title: 'Nome' },
        { field: 'tel', title: 'Telefone' },
        { field: 'email', title: 'E-mail' },
        { field: 'tags', title: 'Tags (demo)' }
      ],
      rows: [
        { nome: 'Marina Costa', tel: '+55 11 9****-**88', email: 'marina.c@email.demo', tags: 'Trabalho, WhatsApp' },
        { nome: 'Carlos Mendes', tel: '+55 11 9****-**72', email: '—', tags: 'Comercial' },
        { nome: 'Delivery Pizza Sul', tel: '4003-9000', email: '—', tags: 'Serviço' }
      ]
    },
    footnote: 'Dados inventados.'
  },

  webHistory: {
    title: 'Histórico de sites (demonstração)',
    subtitle: 'URLs visitadas com título da aba e tempo aproximado.',
    kpis: [
      { label: 'Páginas (24h)', value: '876', hint: 'inclui SPA' },
      { label: 'Domínios únicos', value: '64', hint: '' },
      { label: 'Bloqueados', value: '3', hint: 'categoria adulto (simulado)' }
    ],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'titulo', title: 'Título' },
        { field: 'url', title: 'URL' }
      ],
      rows: [
        { hora: '09:14', titulo: 'Notícias — Economia', url: 'https://news.demo/economia' },
        { hora: '08:58', titulo: 'YouTube — review smartphone', url: 'https://youtube.demo/watch?v=demo' },
        { hora: '08:30', titulo: 'Suporte — FAQ rastreamento', url: 'https://suporte.wtsoftware.com.br/...' }
      ]
    },
    footnote: 'Domínios “.demo” são intencionais.'
  },

  webSearch: {
    title: 'Pesquisas no navegador (demonstração)',
    subtitle: 'Termos digitados em barras de busca e campos de pesquisa.',
    kpis: [
      { label: 'Consultas (7d)', value: '145', hint: '' },
      { label: 'Incômodo / risco', value: '6', hint: 'palavras-chave demo' }
    ],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'motor', title: 'Motor' },
        { field: 'termo', title: 'Termo' }
      ],
      rows: [
        { hora: '09:01', motor: 'Google', termo: 'como ocultar apps android' },
        { hora: '08:22', motor: 'Google', termo: 'melhor antivírus 2026' },
        { hora: 'Ontem', motor: 'YouTube', termo: 'tutorial root (não recomendado)' }
      ]
    },
    footnote: 'Use para treinar olhar de segurança — sem conteúdo real.'
  },

  smsChat: {
    title: 'SMS — mensagens (demonstração)',
    subtitle: 'Operadora fictícia + threads.',
    kpis: [
      { label: 'SMS (7d)', value: '67', hint: '' },
      { label: 'Códigos / banco', value: '9', hint: 'OTP simulados' },
      { label: 'Promoções', value: '31', hint: 'spam' }
    ],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'num', title: 'Número' },
        { field: 'msg', title: 'Mensagem' }
      ],
      rows: [
        { hora: '07:02', num: '28595', msg: 'Seu código Banco Demo é 482910. Não compartilhe.' },
        { hora: 'Ontem', num: '90000', msg: 'Promoção: 30% OFF pizza — responda SIM' }
      ]
    },
    footnote: 'Códigos são aleatórios e inválidos.'
  },

  location: {
    title: 'Localização GPS (demonstração)',
    subtitle: 'Trilha simplificada entre casa → trabalho → academia (fictício).',
    kpis: [
      { label: 'Pontos (24h)', value: '240', hint: 'amostragem 2 min' },
      { label: 'Deslocamento', value: '38 km', hint: 'rota estimada' },
      { label: 'Precisão média', value: '±12 m', hint: 'GPS + Wi‑Fi' }
    ],
    insights: [
      { title: 'Endereços inferidos (demo)', text: 'Casa: Rua das Acácias, 120 — Trabalho: Av. Paulista, 1000 (endereços fictícios).' }
    ],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'lat', title: 'Lat' },
        { field: 'lng', title: 'Lng' },
        { field: 'prec', title: 'Precisão' }
      ],
      rows: [
        { hora: '09:17', lat: '-23.5612', lng: '-46.6550', prec: '10 m' },
        { hora: '08:40', lat: '-23.5488', lng: '-46.6388', prec: '15 m' },
        { hora: '07:55', lat: '-23.5221', lng: '-46.6102', prec: '8 m' }
      ]
    },
    footnote: 'Coordenadas não correspondem a pessoas reais.'
  },

  safeZone: {
    title: 'Zona segura (demonstração)',
    subtitle: 'Geocerca “Casa” e “Escola” com alertas simulados.',
    kpis: [
      { label: 'Zonas ativas', value: '2', hint: '' },
      { label: 'Eventos (7d)', value: '11', hint: 'entrada/saída' },
      { label: 'Tempo em casa', value: '62%', hint: 'aprox.' }
    ],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'zona', title: 'Zona' },
        { field: 'evt', title: 'Evento' }
      ],
      rows: [
        { hora: '08:05', zona: 'Casa', evt: 'Saída detectada' },
        { hora: '08:52', zona: 'Escola', evt: 'Entrada detectada' },
        { hora: '15:10', zona: 'Escola', evt: 'Saída detectada' }
      ]
    },
    footnote: 'Horários escolares meramente ilustrativos.'
  },

  photoGallery: {
    title: 'Galeria de fotos (demonstração)',
    subtitle: 'Metadados de mídia no armazenamento do aparelho.',
    kpis: [
      { label: 'Itens indexados', value: '92', hint: 'miniaturas' },
      { label: 'Vídeos', value: '11', hint: '' },
      { label: 'Screenshots', value: '24', hint: '' }
    ],
    mainTable: {
      columns: [
        { field: 'arquivo', title: 'Arquivo' },
        { field: 'tipo', title: 'Tipo' },
        { field: 'tamanho', title: 'Tamanho' },
        { field: 'pasta', title: 'Pasta' }
      ],
      rows: [
        { arquivo: 'IMG_20260201_081233.jpg', tipo: 'JPEG', tamanho: '3,2 MB', pasta: 'DCIM/Camera' },
        { arquivo: 'VID_20260130_194512.mp4', tipo: 'MP4', tamanho: '42 MB', pasta: 'DCIM/Camera' }
      ]
    },
    footnote: 'Não há upload nem exibição de imagens reais nesta demo.'
  },

  screenshot: {
    title: 'Capturas de tela (demonstração)',
    subtitle: 'Eventos de screenshot detectados (simulado).',
    kpis: [{ label: 'Capturas (7d)', value: '8', hint: '' }],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'app', title: 'App em foco' },
        { field: 'res', title: 'Resolução' }
      ],
      rows: [
        { hora: '08:33', app: 'Chrome', res: '1080x2400' },
        { hora: 'Ontem', app: 'WhatsApp', res: '1080x2400' }
      ]
    },
    footnote: 'Miniaturas reais não são armazenadas na demo.'
  },

  remotePhoto: {
    title: 'Foto remota — v1 (demonstração)',
    subtitle: 'Fila de disparos remotos simulados.',
    kpis: [{ label: 'Na fila', value: '0', hint: '' }, { label: 'Últimas 24h', value: '3', hint: '' }],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'cam', title: 'Câmera' },
        { field: 'res', title: 'Resultado' }
      ],
      rows: [
        { hora: '08:01', cam: 'Traseira', res: 'OK — 4,2 MB' },
        { hora: 'Ontem 19:40', cam: 'Frontal', res: 'OK — 2,1 MB' }
      ]
    },
    footnote: 'Sem transmissão de imagem nesta interface de demonstração.'
  },

  remotePhotoV2: {
    title: 'Foto remota — v2 (demonstração)',
    subtitle: 'Mesmo fluxo com metadados adicionais.',
    kpis: [{ label: 'Disparos', value: '3', hint: '24h' }],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'lente', title: 'Lente' },
        { field: 'exif', title: 'EXIF (demo)' }
      ],
      rows: [{ hora: '08:01', lente: 'Wide', exif: 'ISO200 · 1/120s' }]
    },
    footnote: 'Versão painel 1+.'
  },

  microphone: {
    title: 'Microfone ambiente (demonstração)',
    subtitle: 'Trechos de áudio ambiente indexados (metadados).',
    kpis: [
      { label: 'Gravações (24h)', value: '15', hint: 'fragmentos' },
      { label: 'Duração total', value: '42m', hint: '' }
    ],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'dur', title: 'Duração' },
        { field: 'ruido', title: 'Nível médio' },
        { field: 'tags', title: 'Tags (demo)' }
      ],
      rows: [
        { hora: '08:15', dur: '3m', ruido: 'Moderado', tags: 'TV ao fundo' },
        { hora: '07:50', dur: '5m', ruido: 'Baixo', tags: 'Conversa próxima' }
      ]
    },
    footnote: 'Áudio não é reproduzido nesta página de demo.'
  },

  desktopDashboard: {
    title: 'Computador — visão geral (demonstração)',
    subtitle: 'Estação “DESKTOP-DEMO-01” · Windows 11 (simulado).',
    kpis: [
      { label: 'Screenshots (24h)', value: '48', hint: '' },
      { label: 'URLs HTTP', value: '312', hint: '' },
      { label: 'Teclas', value: '4.902', hint: '' }
    ],
    mainTable: {
      columns: [
        { field: 'evt', title: 'Evento' },
        { field: 'det', title: 'Detalhe' }
      ],
      rows: [
        { evt: 'Login Windows', det: '08:02 — usuário Ana.Demo' },
        { evt: 'Chrome iniciado', det: '08:05' },
        { evt: 'USB conectado', det: '10:12 — pendrive “BACKUP”' }
      ]
    },
    footnote: 'Eventos de SO meramente ilustrativos.'
  },

  desktopSites: {
    title: 'Sites — computador (demonstração)',
    subtitle: 'Tráfego HTTP capturado (simulado).',
    kpis: [{ label: 'Hosts', value: '38', hint: '24h' }],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'host', title: 'Host' },
        { field: 'path', title: 'Caminho' }
      ],
      rows: [
        { hora: '09:10', host: 'cdn.demo', path: '/assets/app.js' },
        { hora: '08:44', host: 'intranet.corp.demo', path: '/wiki/onboarding' }
      ]
    },
    footnote: ''
  },

  desktopKeys: {
    title: 'Teclas — computador (demonstração)',
    subtitle: 'Captura de teclado físico (fictício).',
    kpis: [{ label: 'Teclas (24h)', value: '4.902', hint: '' }],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'janela', title: 'Janela' },
        { field: 'seq', title: 'Sequência (demo)' }
      ],
      rows: [
        { hora: '09:02', janela: 'Outlook — novo e-mail', seq: 'Assunto: Reunião Q1' },
        { hora: '08:50', janela: 'Excel — Planilha1', seq: '=SOMA(A1:A99)' }
      ]
    },
    footnote: ''
  },

  desktopSearch: {
    title: 'Pesquisas — computador (demonstração)',
    subtitle: 'Consultas em buscadores e campos do sistema.',
    kpis: [{ label: 'Consultas', value: '56', hint: '7d' }],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'app', title: 'Origem' },
        { field: 'q', title: 'Consulta' }
      ],
      rows: [
        { hora: '08:18', app: 'Edge', q: 'política de senhas AD' },
        { hora: 'Ontem', app: 'Explorador', q: 'contrato.docx' }
      ]
    },
    footnote: ''
  },

  desktopScreens: {
    title: 'Telas — computador (demonstração)',
    subtitle: 'Capturas programadas da área de trabalho.',
    kpis: [{ label: 'Imagens (24h)', value: '48', hint: '' }],
    mainTable: {
      columns: [
        { field: 'hora', title: 'Hora' },
        { field: 'res', title: 'Resolução' },
        { field: 'mon', title: 'Monitor' }
      ],
      rows: [
        { hora: '09:00', res: '1920x1080', mon: '1 — principal' },
        { hora: '08:30', res: '1920x1080', mon: '1 — principal' }
      ]
    },
    footnote: ''
  },

  devices: {
    title: 'Meus dispositivos (demonstração)',
    subtitle: 'Lista de licenças e aparelhos vinculados à conta fictícia.',
    kpis: [
      { label: 'Mobile', value: '2', hint: '1 ativo demo' },
      { label: 'Desktop', value: '1', hint: '' },
      { label: 'Expirando (30d)', value: '0', hint: '' }
    ],
    mainTable: {
      columns: [
        { field: 'nome', title: 'Apelido' },
        { field: 'tipo', title: 'Tipo' },
        { field: 'lic', title: 'Licença até' },
        { field: 'st', title: 'Status' }
      ],
      rows: [
        { nome: 'Celular de demonstração', tipo: 'Android', lic: '31/12/2030', st: 'Online (simulado)' },
        { nome: 'Filho — backup', tipo: 'Android', lic: '15/06/2026', st: 'Offline 2h' },
        { nome: 'Notebook trabalho', tipo: 'Windows', lic: '01/03/2027', st: 'Online' }
      ]
    },
    footnote: 'Nenhum dispositivo real está vinculado.'
  },

  subscriptions: {
    title: 'Assinaturas (demonstração)',
    subtitle: 'Planos e cobranças simuladas.',
    kpis: [
      { label: 'Plano ativo', value: 'Premium Demo', hint: 'ciclo mensal' },
      { label: 'Próxima cobrança', value: 'R$ 89,90', hint: '12/02/2026' },
      { label: 'Método', value: 'Cartão •••• 4242', hint: 'sandbox' }
    ],
    mainTable: {
      columns: [
        { field: 'ref', title: 'Ref' },
        { field: 'desc', title: 'Descrição' },
        { field: 'val', title: 'Valor' },
        { field: 'st', title: 'Status' }
      ],
      rows: [
        { ref: 'INV-9001', desc: 'Licença mobile — jan/26', val: 'R$ 89,90', st: 'Pago' },
        { ref: 'INV-9000', desc: 'Licença mobile — dez/25', val: 'R$ 89,90', st: 'Pago' }
      ]
    },
    footnote: 'Faturas inventadas.'
  },

  newDevice: {
    title: 'Novo dispositivo (demonstração)',
    subtitle: 'Fluxo de emparelhamento — apenas texto de apoio.',
    kpis: [
      { label: 'Passo atual', value: '3 / 5', hint: 'instalação APK' },
      { label: 'Tempo estimado', value: '8 min', hint: '' }
    ],
    insights: [
      { title: 'Checklist (demo)', text: '1) Liberar fontes desconhecidas · 2) Instalar APK demo · 3) Conceder permissões de acessibilidade · 4) Ocultar ícone (opcional) · 5) Validar painel online.' }
    ],
    mainTable: {
      columns: [
        { field: 'passo', title: 'Passo' },
        { field: 'acao', title: 'Ação' }
      ],
      rows: [
        { passo: 'Download', acao: 'Link magnético simulado (não clicável na demo)' },
        { passo: 'Permissões', acao: '18 permissões típicas do app real' }
      ]
    },
    footnote: 'Siga o fluxo real após o login.'
  },

  transferLicence: {
    title: 'Transferir licença (demonstração)',
    subtitle: 'Movimentação de licença entre contas — simulação.',
    kpis: [{ label: 'Licenças elegíveis', value: '2', hint: '' }],
    mainTable: {
      columns: [
        { field: 'chave', title: 'Chave (demo)' },
        { field: 'prod', title: 'Produto' },
        { field: 'sit', title: 'Situação' }
      ],
      rows: [
        { chave: 'WS-DEMO-AAAA-BBBB', prod: 'Mobile Premium', sit: 'Transferível' },
        { chave: 'WS-DEMO-CCCC-DDDD', prod: 'Mobile Light', sit: 'Em uso' }
      ]
    },
    footnote: 'Chaves não são válidas.'
  },

  transferDevices: {
    title: 'Transferir dispositivos (demonstração)',
    subtitle: 'Mover aparelho entre licenças do mesmo titular.',
    kpis: [{ label: 'Dispositivos', value: '3', hint: '' }],
    mainTable: {
      columns: [
        { field: 'apelido', title: 'Apelido' },
        { field: 'orig', title: 'Licença origem' },
        { field: 'dest', title: 'Licença destino' }
      ],
      rows: [
        { apelido: 'Celular de demonstração', orig: 'WS-DEMO-AAAA', dest: '—' }
      ]
    },
    footnote: ''
  },

  accountProfile: {
    title: 'Conta — perfil (demonstração)',
    subtitle: 'Dados cadastrais fictícios.',
    kpis: [
      { label: '2FA', value: 'Ativo (simulado)', hint: 'Google Authenticator' },
      { label: 'E-mail verificado', value: 'Sim', hint: '' }
    ],
    mainTable: {
      columns: [
        { field: 'campo', title: 'Campo' },
        { field: 'valor', title: 'Valor (demo)' }
      ],
      rows: [
        { campo: 'Nome', valor: 'Usuário Demonstração' },
        { campo: 'E-mail', valor: 'demo.usuario@email.demo' },
        { campo: 'Telefone', valor: '+55 11 9****-**00' },
        { campo: 'Fuso', valor: 'America/Sao_Paulo' }
      ]
    },
    footnote: 'Edição desabilitada na rota de demonstração.'
  }
}

function genericDemo(view) {
  const label = String(view || 'tela').replace(/([A-Z])/g, ' $1').trim()
  return {
    title: `${label} (demonstração)`,
    subtitle: SYNC,
    kpis: [{ label: 'Modo demo', value: 'Ativo', hint: 'sem API' }],
    insights: [{ title: 'Conteúdo genérico', text: 'Esta tela usa dados fictícios padrão. Detalhes específicos podem ser adicionados depois.' }],
    mainTable: {
      columns: [
        { field: 'a', title: 'Item' },
        { field: 'b', title: 'Detalhe' }
      ],
      rows: [
        { a: 'Exemplo A', b: 'Valor fictício 1' },
        { a: 'Exemplo B', b: 'Valor fictício 2' }
      ]
    },
    footnote: 'Rota de demonstração — faça login para usar a versão completa.'
  }
}

export function getDemoScreen(view, locale) {
  const raw = !view ? genericDemo('painel') : RICH[view] || genericDemo(view)
  return localizeDemoRich(raw, locale)
}
